/**
 * The TimeFormatter class is for the V10 input field of type "TIMEBOX" (Aurion.form.TimeField)
 * and is used in most standard forms, surveys, and TK forms.
 *
 *     <TIMEBOX ... allowGt24Hrs="true" />
 *     <COLUMN  ... type="TIMEBOX" interval="15">
 */
export class TimeFormatter {
    constructor(element) {
        this.input = element;
        this.el = $(element);

        this.el // keydown+, keypress?, input?, keyup, change?
            .on('keypress', (e) => this.handleOnKeypress(e))
            .on('focus', (e) => this.handleOnFocus(e))
            .on('blur', (e) => this.handleOnBlur(e));

        this.el.attr('placeholder', this.getPlaceholder());
    }

    handleOnKeypress(e) {
        /**
            e.charCode:  0, e.char: '',   e.keyCode:  8,   e.key: 'Backspace'
            e.charCode:  0, e.char: '',   e.keyCode:  9,   e.key: 'Tab'
            e.charCode: 32, e.char: ' ',  e.keyCode: 32,   e.key: 'Spacebar'
         */
        const key = String.fromCharCode(e.charCode);

        if (!key || e.altKey || e.ctrlKey || e.shiftKey) return;

        if (!e.charCode || key.length !== 1) {
            // MSIE includes key events such as "Tab" and "Backspace"
            return;
        }

        // When the user presses down on a key ...

        // ... ignore the key if it's not a valid number or separator

        if (!key.match(this.constructor.keypressPattern)) {
            e.preventDefault();
            return false;
        }

        // ... or if it's a separator, only allow it once

        const isSep = key === ':' || key === ' ' || key === '.';
        const { value } = this.input;

        if (isSep) {
            if (value.match(/^(-)?\d+$/)) {
                this.el.val(`${value}:`).change();
            }

            e.preventDefault();
            return false;
        }
    }

    handleOnFocus(_e) {
        this._valueOnBlur = this.input.value;
    }

    handleOnBlur(_e) {
        const startingValue = this.input.value;

        const v = startingValue.trim();

        if (!v) {
            this.el.toggleClass('form-control-danger', this.el.prop('required'));
            return;
        }

        if (this._valueOnBlur && v === this._valueOnBlur.trim()) {
            // Bail if the new value looks the same.
            return;
        }

        const formattedValue = this.reformatValue(v);

        if (formattedValue !== v) {
            this.el.val(formattedValue);
        }

        this.el.toggleClass('form-control-danger', !this.isValidValue(formattedValue));

        if (this.input.value !== startingValue) {
            this.el.change();
        }
    }

    reformatValue(value) {
        const interval = Number(this.el.attr('data-time-interval') || 0);
        return this.constructor.reformat(value, interval, this.el.data('timeAllowgt24hrs'));
    }

    isValidValue(newValue) {
        return this.constructor.isValid(newValue, this.el.data('timeAllowgt24hrs'));
    }

    getPlaceholder() {
        return 'hh:mm';
    }

    /**
     * Reformat the time value after the user has left the field.
     *
     * Valid input formats are:
     *    * HH:MM
     *    * HH MM
     *    * HHMM
     *    * HH
     *
     * @param  {String} value       [description]
     * @param  {Number} interval    The step value to snap minutes to. eg: 15, 10, 30.
     *
     * @return {String}             If an invalid value was provided the same value will
     *                              be returned.
     */
    static reformat(value, interval, allowGt24Hrs) {
        let matches;

        // Handle the HHMM, HMM and HH cases first
        if ((matches = value.match(/^(\d{1,2})(\d{2})?$/))) {
            value = `${matches[1]}:${matches[2] || '00'}`;
        }

        if ((matches = value.match(/^(\d+)?([:. ]+)?(\d+)?$/))) {
            let [, hrs, , min] = matches;
            hrs = Number(hrs || 0);
            min = Number(min || 0);

            if (interval && Number.isInteger(interval) && 0 < interval) {
                const overage = min % interval; // eg: 12 % 15 => 12 | 41 % 15 => 11
                const midway = interval / 2; // eg: 15 / 2 => 7.5
                const floor = Math.floor(min / interval) * interval; // eg: 0 | 30
                min = overage < midway ? floor : floor + interval;
            }

            if (59 < min) {
                const newMins = min % 60;
                hrs += (min - newMins) / 60;
                min = newMins;
            }

            if (min < 10) min = `0${min}`;

            value = `${hrs}:${min}`;
        }

        // '24:00' is currently permitted by default in timesheets but not surveys
        return allowGt24Hrs === false && value === '24:00' ? '0:00' : value;
    }

    static isValid(value, allowGt24Hrs) {
        const matches = value.match(/^(\d+):(\d{2})$/);
        if (!matches) return false;

        let [, hrs, min] = matches;
        hrs = Number(hrs);
        min = Number(min);

        // '24:00' is currently permitted by default in timesheets but not surveys
        return !!(
            matches &&
            min < 60 &&
            (hrs < 24 || (allowGt24Hrs !== false && value === '24:00') || allowGt24Hrs)
        );
    }
}

TimeFormatter.keypressPattern = /^[:. 0-9]$/;

/**
 * The HoursMinsFormatter class is for the V10 input field of type "HOURSMINSBOX"
 * (Aurion.form.HourMinField) and is used in surveys.
 *
 *      <QUESTION type="HOURSMINSBOX">
 *      <COLUMN type="HOURSMINSBOX">
 *
 * Note that this class does not support the allowGt24Hrs or interval options
 * as they appear to have never been used in V10.
 */
export class HoursMinsFormatter extends TimeFormatter {
    getPlaceholder() {
        return '(-)hh:mm';
    }

    /**
     * Reformat the time value after the user has left the field.
     *
     * Valid input formats are:
     *     * -HHHHHH:MM
     *     * -HHHHHH MM
     *     * -HHHHHH.MM
     *       [ From V10:    /^-?\d{1,8}([\: \.]\d{1,2})?$/ ]
     *
     * @param  {String} value       [description]
     * @return {String}             If an invalid value was provided the same value will
     *                              be returned.
     */
    static reformat(value) {
        let matches;

        if ((matches = value.match(/^(-)?(\d{1,8})(?:([: .])(\d+)?)?$/))) {
            let [, neg, hrs, , min] = matches;

            neg = neg || '';
            hrs = Number(hrs);
            min = Number(min || 0);

            if (59 < min) {
                const newMins = min % 60;
                hrs += (min - newMins) / 60;
                min = newMins;
            }

            if (min < 10) min = `0${min}`;

            return `${neg}${hrs}:${min}`;
        }

        return value;
    }

    static isValid(value) {
        const matches = value.match(/^(-)?(\d+):(\d{2})$/);
        return matches && Number(matches[3]) < 60;
    }
}

HoursMinsFormatter.keypressPattern = /^[-:. 0-9]$/;

function init(addedNodes) {
    const clsTypeTime = 'form-input-time';
    const clsTypeHours = 'form-input-hoursmins';

    addedNodes.forEach((addedNode) => {
        $(addedNode)
            .find(`.${clsTypeTime}, .${clsTypeHours}`)
            .each((_idx, node) => {
                if (node._timeFieldFormatter) return;

                node._timeFieldFormatter = $(node).hasClass(clsTypeHours)
                    ? new HoursMinsFormatter(node)
                    : new TimeFormatter(node);
            });
    });
}

if (window.util) {
    util.initialiseAddedNodes(init);
}
